import { useState, useEffect } from 'react'
import {
  IconButton,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  MenuItem,
  Menu,
} from '@mui/material'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import DownloadIcon from '@mui/icons-material/Download'
import { useBoolean } from 'hooks/useBoolean'
import { useTranslation } from 'react-i18next'
import { LoadingButton } from '@mui/lab'
import { useDownloadFileMutation } from 'services/file'
import { downloadFile as downloadFileHandler } from 'utils/download-file'
import { toast } from 'react-toastify'
import { DialogWatchVideo } from './dialog-watch-video'
import { useDeleteVideoInstructionMutation } from 'services/video-instructions'
import ConfirmationDialog from 'components/ConfirmationDialog'
import { VideoFieldValues } from './video-form'
import { DialogEditVideo } from './dialog-edit-video'
import { parseYouTubeVideoId } from 'utils/youtube-video-url-parser'

type Props = {
  id: string
} & VideoFieldValues

export const VideoInstructionsCard = ({
  id,
  title,
  description,
  videoUrl,
  instructionFileId,
  ordinal,
  audienceOrganizationType,
}: Props) => {
  const { t } = useTranslation()

  const dialogWatchVideo = useBoolean(false)
  const dialogEditVideo = useBoolean(false)
  const dialogDeleteVideo = useBoolean(false)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const [downloadFile, { isLoading, isError }] = useDownloadFileMutation()
  const [deleteVideoInstruction, { isSuccess, isError: isDeleteError }] =
    useDeleteVideoInstructionMutation()

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError || isDeleteError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, isDeleteError, t])

  const handleClose = () => {
    setAnchorEl(null)
  }

  const onClickDownload = () => {
    downloadFile({ id: instructionFileId }).then((blob) => {
      downloadFileHandler(blob, `Telimat-${title}`)
    })
  }

  const youtubeVideoThumbnail = `http://img.youtube.com/vi/${parseYouTubeVideoId(videoUrl)}/mqdefault.jpg`

  return (
    <>
      <ConfirmationDialog
        open={dialogDeleteVideo.isTrue}
        onClose={dialogDeleteVideo.setFalse}
        onConfirm={() => {
          deleteVideoInstruction({ id })
        }}
        text={t('are-you-sure-you-want-to-delete')}
      />
      {dialogEditVideo.isTrue && (
        <DialogEditVideo
          id={id}
          defaultValues={{
            title,
            description,
            videoUrl,
            audienceOrganizationType,
            instructionFileId,
            ordinal,
          }}
          onClose={dialogEditVideo.setFalse}
        />
      )}
      {dialogWatchVideo.isTrue && (
        <DialogWatchVideo videoUrl={videoUrl} onClose={dialogWatchVideo.setFalse} />
      )}
      <Menu
        id="video-edit-options"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <MenuItem
          onClick={() => {
            handleClose()
            dialogEditVideo.setTrue()
          }}
        >
          {t('edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleClose()
            dialogDeleteVideo.setTrue()
          }}
        >
          {t('delete')}
        </MenuItem>
      </Menu>

      <Card>
        <CardHeader
          title={title}
          titleTypographyProps={{
            noWrap: true,
            textOverflow: 'ellipsis',
          }}
          sx={{
            '& .MuiCardHeader-content': {
              display: 'block',
              overflow: 'hidden',
              color: 'primary.main',
            },
          }}
          action={
            <IconButton
              onClick={(event) => setAnchorEl(event.currentTarget)}
              id="video-edit-options"
              aria-label="settings"
            >
              <MoreVertIcon />
            </IconButton>
          }
        />

        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            height: 175,
            background: `url(${youtubeVideoThumbnail})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'contain',
            backgroundPosition: 'center',
          }}
        >
          <IconButton onClick={dialogWatchVideo.setTrue} sx={{ padding: 0 }}>
            <PlayCircleOutlineIcon sx={{ fontSize: 60, color: '#fff' }} />
          </IconButton>
        </Stack>
        <CardContent>
          <Typography
            sx={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {description}
          </Typography>
        </CardContent>
        <CardActions>
          <Stack
            width="100%"
            alignItems="center"
            direction="row"
            justifyContent="space-between"
          >
            <LoadingButton
              disabled={instructionFileId === null}
              onClick={onClickDownload}
              loading={isLoading}
            >
              <DownloadIcon />
              {t('downloadInstruction')}
            </LoadingButton>
          </Stack>
        </CardActions>
      </Card>
    </>
  )
}
